import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreVertical, UserMinus } from 'lucide-react';
import { useState } from 'react';

import { removeMembro, updateMembroRole, type EscritorioMembro } from '@/api/client';
import { definirGestor } from '@/api/distribuicao';
import type { Equipe } from '@/api/equipes';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useNotification } from '@/context/NotificationProvider';
import { notifyMutationError } from '@/lib/errors';
import { formatDate } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { corDoTexto, corValida } from '@/lib/tags/cores';

import { Avatar, podeGerir, RoleBadge } from './papeis';

interface MembrosAbaProps {
  escritorioId: string;
  membros: EscritorioMembro[];
  equipes: Equipe[];
  meuPapel: string;
  meuId: string | undefined;
}

/** A hierarquia do backend (`ROLE_HIERARCHY`): só se mexe em quem está abaixo. */
const NIVEL: Record<string, number> = {
  PROPRIETARIO: 3,
  SOCIO: 2,
  ASSOCIADO: 1,
  ESTAGIARIO: 0,
};
const ROTULO_DA_TROCA: Record<string, string> = {
  SOCIO: 'Tornar Sócio',
  ASSOCIADO: 'Tornar Associado',
  ESTAGIARIO: 'Tornar Estagiário',
};

/** As trocas de papel que quem consulta pode fazer neste membro — as mesmas regras do
 * backend: o alvo e o papel novo ficam abaixo do de quem troca. */
function trocasDePapel(meuPapel: string, membro: EscritorioMembro): { para: string; rotulo: string }[] {
  const meu = NIVEL[meuPapel] ?? 0;
  if ((NIVEL[membro.role] ?? 0) >= meu) return [];
  return ['SOCIO', 'ASSOCIADO', 'ESTAGIARIO']
    .filter((p) => p !== membro.role && NIVEL[p] < meu)
    .map((p) => ({ para: p, rotulo: ROTULO_DA_TROCA[p] }));
}

function podeRemover(meuPapel: string, membro: EscritorioMembro): boolean {
  return podeGerir(meuPapel) && (NIVEL[membro.role] ?? 0) < (NIVEL[meuPapel] ?? 0);
}

function ChipsDeEquipe({ equipes }: { equipes: Equipe[] }) {
  if (equipes.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {equipes.map((e) => (
        <span
          key={e.id}
          className="rounded-full px-2 py-0.5 text-[11px] font-medium leading-4"
          style={{
            backgroundColor: corValida(e.cor),
            color: corDoTexto(e.cor),
          }}
        >
          {e.nome}
        </span>
      ))}
    </span>
  );
}

/** O selo de quem distribui; no Proprietário, que é gestor sempre, não precisa. */
function SeloGestor({ membro }: { membro: EscritorioMembro }) {
  if (!membro.gestor || membro.role === 'PROPRIETARIO') return null;
  return (
    <span className="rounded border px-1.5 py-0.5 text-[11px] font-medium leading-4 text-muted-foreground">Gestor</span>
  );
}

export function MembrosAba({ escritorioId, membros, equipes, meuPapel, meuId }: MembrosAbaProps) {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useNotification();
  const [aRemover, setARemover] = useState<EscritorioMembro | null>(null);

  const recarregar = () => {
    queryClient.invalidateQueries({
      queryKey: queryKeys.escritorios.detail(escritorioId),
    });
    queryClient.invalidateQueries({
      queryKey: queryKeys.escritorios.equipes(escritorioId),
    });
    queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.all });
    // Remover encerra as delegações do membro naquele escritório.
    queryClient.invalidateQueries({ queryKey: ['delegacoes'] });
  };
  const trocar = useMutation({
    mutationFn: ({ membroId, papel }: { membroId: string; papel: string }) =>
      updateMembroRole(escritorioId, membroId, papel),
    onSuccess: () => {
      showSuccess('Papel atualizado');
      recarregar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  // Gestor (§19): só o Proprietário marca; o Proprietário já é gestor.
  const marcarGestor = useMutation({
    mutationFn: ({ membro, gestor }: { membro: EscritorioMembro; gestor: boolean }) =>
      definirGestor(escritorioId, membro.id, gestor),
    onSuccess: (_r, { membro, gestor }) => {
      showSuccess(gestor ? `${membro.usuario_nome} agora é gestor` : `${membro.usuario_nome} deixou de ser gestor`);
      queryClient.invalidateQueries({ queryKey: queryKeys.escritorios.detail(escritorioId) });
    },
    onError: (err) => notifyMutationError(err, showError),
  });
  const remover = useMutation({
    mutationFn: (membroId: string) => removeMembro(escritorioId, membroId),
    onSuccess: () => {
      showSuccess('Membro removido do escritório');
      setARemover(null);
      recarregar();
    },
    onError: (err) => notifyMutationError(err, showError),
  });

  const equipesDe = (usuarioId: string) => equipes.filter((e) => e.membros.some((m) => m.usuario_id === usuarioId));
  // O Proprietário primeiro, depois por nome.
  const ordenados = [...membros].sort(
    (a, b) =>
      Number(b.role === 'PROPRIETARIO') - Number(a.role === 'PROPRIETARIO') ||
      a.usuario_nome.localeCompare(b.usuario_nome, 'pt-BR'),
  );

  const menu = (m: EscritorioMembro) => {
    const trocas = trocasDePapel(meuPapel, m);
    const remove = podeRemover(meuPapel, m);
    const marcaGestor = meuPapel === 'PROPRIETARIO' && m.role !== 'PROPRIETARIO';
    if (m.usuario_id === meuId || (trocas.length === 0 && !remove && !marcaGestor)) return null;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-11 w-11 sm:h-8 sm:w-8"
            aria-label={`Ações de ${m.usuario_nome}`}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {trocas.map((t) => (
            <DropdownMenuItem key={t.para} onClick={() => trocar.mutate({ membroId: m.id, papel: t.para })}>
              {t.rotulo}
            </DropdownMenuItem>
          ))}
          {marcaGestor && (
            <>
              {trocas.length > 0 && <DropdownMenuSeparator />}
              <DropdownMenuCheckboxItem
                checked={!!m.gestor}
                onCheckedChange={(v) => marcarGestor.mutate({ membro: m, gestor: v === true })}
              >
                Gestor (distribui os processos)
              </DropdownMenuCheckboxItem>
            </>
          )}
          {(trocas.length > 0 || marcaGestor) && remove && <DropdownMenuSeparator />}
          {remove && (
            <DropdownMenuItem className="text-destructive focus:text-destructive" onClick={() => setARemover(m)}>
              <UserMinus className="mr-2 h-4 w-4" />
              Remover do escritório
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const nome = (m: EscritorioMembro) => (
    <span className="flex min-w-0 items-center gap-3">
      <Avatar nome={m.usuario_nome} />
      <span className="min-w-0">
        <span className="block break-words text-sm font-medium">
          {m.usuario_nome}
          {m.usuario_id === meuId && <span className="ml-1.5 text-xs font-normal text-muted-foreground">(você)</span>}
        </span>
        <span className="block [overflow-wrap:anywhere] text-xs text-muted-foreground">{m.usuario_email}</span>
      </span>
    </span>
  );

  return (
    <>
      {/* Celular: cartões */}
      <div className="space-y-2 sm:hidden">
        {ordenados.map((m) => (
          <Card key={m.id} className="p-3">
            <div className="flex items-start justify-between gap-2">
              {nome(m)}
              {menu(m)}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <RoleBadge role={m.role} />
              <SeloGestor membro={m} />
              <ChipsDeEquipe equipes={equipesDe(m.usuario_id)} />
            </div>
          </Card>
        ))}
      </div>

      {/* Computador: tabela */}
      <Card className="hidden sm:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Membro</TableHead>
              <TableHead className="w-36">Papel</TableHead>
              <TableHead>Equipes</TableHead>
              <TableHead className="w-28">Desde</TableHead>
              <TableHead className="w-12">
                <span className="sr-only">Ações</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {ordenados.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{nome(m)}</TableCell>
                <TableCell>
                  <span className="flex flex-wrap items-center gap-1.5">
                    <RoleBadge role={m.role} />
                    <SeloGestor membro={m} />
                  </span>
                </TableCell>
                <TableCell>
                  <ChipsDeEquipe equipes={equipesDe(m.usuario_id)} />
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">{formatDate(m.created_at)}</TableCell>
                <TableCell className="text-right">{menu(m)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <ConfirmDialog
        open={!!aRemover}
        title="Remover do escritório"
        message={
          <>
            <strong>{aRemover?.usuario_nome}</strong> deixa de ver o que o escritório compartilha e sai das equipes. O que
            foi compartilhado fica no escritório, só para leitura.
          </>
        }
        confirmLabel="Remover"
        confirmColor="error"
        loading={remover.isPending}
        onConfirm={() => aRemover && remover.mutate(aRemover.id)}
        onCancel={() => setARemover(null)}
      />
    </>
  );
}
